import { UnsavedUser, User } from '@paragon/contract-library';
import { userService } from '@paragon/data-connections';
import { getLoggedInUser } from '@paragon/login-tools';
import { enqueueError, enqueueSuccess, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { usersAdd, usersMerge, usersSet } from '../store/userDataSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export const createUser =
  (newUser: UnsavedUser): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const allUserEmails = Object.keys(state.userData.users);
    const newUserEmailAlreadyExists = allUserEmails.includes(newUser.email);
    if (newUserEmailAlreadyExists) {
      dispatch(
        enqueueWarning(`A user with email ${newUser.email} already exists, so the new user will not be created.`),
      );
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage('Creating user...'));

    try {
      const user = await userService.users.create(newUser);
      dispatch(usersAdd(user));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error creating user.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const loadUser =
  (userEmail: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading user data...'));

    try {
      dispatch(usersAdd(await userService.users.fetch(userEmail)));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const loadAllUsersAsRoot = (): AsyncThunkActionVoid => async (dispatch) => {
  try {
    const users = await userService.users.fetchAll();
    dispatch(usersSet(users));
  } catch (err) {
    console.error(err);
  }
};

export type UpdatedUserData = Partial<User> &
  Pick<User, 'email' | 'phone' | 'firstName' | 'lastName' | 'organization' | 'roleSet' | 'location' | 'trialEndDate'>;

export const updateUser =
  (updatedData: UpdatedUserData, emailOfUserToUpdate: string | null = null): AsyncThunkActionReturn<User | null> =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Updating user(s)...'));
    const oldUser = getState().userData.users[updatedData.email];

    try {
      const user = await userService.users.update(
        updatedData.email,
        updatedData.firstName,
        updatedData.lastName,
        updatedData.phone,
        updatedData.roleSet,
        updatedData.organization,
        updatedData.location,
        updatedData.trialEndDate,
      );

      dispatch(usersMerge({ emailOfUserToUpdate, updatedData }));

      // Warn if changing to Individual Designer role and no Stripe subscription exists; the trial will either be
      // indefinite or the user will be downgraded in short order if they already had a trial and it expired. If this
      // becomes common enough of a case we can add controls for it.
      const roleChangedToIndividualDesigner =
        oldUser.roleSet.role !== 'Individual Designer' && updatedData.roleSet.role === 'Individual Designer';
      if (
        roleChangedToIndividualDesigner &&
        getState().organizations.find((o) => o.name === user.organization)?.stripeSubscription === null
      ) {
        const trialEndDate = user.trialEndDate ? new Date(user.trialEndDate) : null;
        if (trialEndDate === null) {
          dispatch(
            enqueueWarning(
              `User ${user.email} has been changed to an Individual Designer role but has no trial period. Contact a developer to set a trial end date.`,
            ),
          );
        } else if (trialEndDate > new Date()) {
          dispatch(
            enqueueWarning(
              `User ${user.email} has been changed to an Individual Designer role but previously had a free trial. To avoid this user being downgraded, contact a developer to set a new trial end date.`,
            ),
          );
        }
      }

      return user;
    } catch (error) {
      dispatch(enqueueError('Error saving changes to user.'));
      console.error(error);
      return null;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export function startFreeTrial(forEmail: string): AsyncThunkActionReturn<User | null> {
  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Starting free trial...'));
    try {
      const user = await userService.users.startFreeTrial(forEmail);
      dispatch(usersMerge({ emailOfUserToUpdate: forEmail, updatedData: user }));
      return user;
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error starting free trial.'));
      return null;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export const updatePassword =
  (newPassword: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const user = getLoggedInUser(getState());
    try {
      await userService.users.updatePassword(user!.email, newPassword);
      dispatch(enqueueSuccess('Password successfully changed!'));
    } catch (error) {
      dispatch(enqueueError('Error changing your password.'));
      console.error(error);
    }
  };
